import React, { useState, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import { documentSlice } from 'rootStateHandler/selectors';
import { FaFolder } from 'react-icons/fa';
import { ChevronRight, ChevronDown, FolderPlus, Trash } from 'react-bootstrap-icons';
import {
  createFolderThunk,
  deleteFolderThunk,
  renameFolderThunk,
  listDocumentsThunk,
  moveDocumentThunk,
  listFolderTreeThunk,
} from 'components/Dashboard/Documents/documentStates/documentThunk';
import Modal from 'components/common/Modal';
import Button from 'components/common/FormItems/Button';
import styled from 'styled-components';
import styles from './fileTree.module.css';
import { setSelectedFolder } from 'components/Dashboard/Documents/documentStates/documentSlice';

const StyledButton = styled(Button)`
  background: ${(props) => (props.background ? '#d5e4f7' : '#4c81be')};
  border: 3px solid #1d579b;
  border-radius: 2px;
  color: #ffffff;
  font-weight: 500;
  height: 38px;
  border: none;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all ease-in-out 300ms;
  white-space: nowrap;
  z-index: 10;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    color: #fff;
    opacity: 0.7;
  }
  &:hover {
    box-shadow: 0 4px 12px rgba(76, 129, 190, 0.5);
    background: #3b6bb3;
    transform: translateY(-1px);
  }
`;

export function FileTree() {
  const { list_folder_tree, selectedFolder } = useSelector(documentSlice);
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [parentId, setParentId] = useState(null);

  const handleCreate = async () => {
    if (!folderName.trim()) return;

    dispatch(createFolderThunk(folderName, parentId));
    setFolderName('');
    setShowModal(false);
    setParentId(null);
  };

  const handleFolderSelect = (folder) => {
    setParentId(folder.id);
    dispatch(setSelectedFolder(folder));
    dispatch(listDocumentsThunk());
  };

  const handleClickOutside = () => {
    setParentId(null);
    const myDrive = list_folder_tree.find((f) => f.is_root);
    if (myDrive) {
      dispatch(setSelectedFolder(myDrive));
      dispatch(listDocumentsThunk());
    }
  };

  const handleDelete = () => {
    if (!selectedFolder || selectedFolder.is_root) {
      alert('My Drive cannot be deleted');
      return;
    }

    const message = `Deleting "${selectedFolder.name}" will also delete all subfolders if any and this action can not be reversed.\n\nAll files will be moved to My Drive.`;

    if (window.confirm(message)) {
      dispatch(deleteFolderThunk(selectedFolder.id));
      setSelectedFolder(null);
      setParentId(null);
      //Set selected folder to my drive after any folder is deleted.
      handleClickOutside();
    }
  };

  return (
    <>
      <div
        className={styles.container}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClickOutside();
        }}
      >
        <div className={styles.inner}>
          <div className={styles.buttonGroup}>
            <StyledButton
              reactIcon={<FolderPlus color="#4C81BE" size={20} />}
              background="#d5e4f7"
              onClick={() => {
                setShowModal(true);
              }}
            >
              New Folder
            </StyledButton>

            <StyledButton
              reactIcon={<Trash color="#ef4444" size={20} />}
              background="#fee2e2"
              onClick={() => {
                handleDelete();
              }}
              disabled={!selectedFolder}
            >
              Delete
            </StyledButton>
          </div>

          {/* Tree */}
          <div style={{ marginTop: '8px' }}>
            {list_folder_tree?.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                selectedFolder={selectedFolder}
                onFolderSelect={handleFolderSelect}
              />
            ))}
          </div>
        </div>
      </div>

      <Modal
        toggleModal={showModal}
        hasInput
        label="Folder Name"
        value={folderName}
        handleChange={(e) => {
          setFolderName(e.target.value);
        }}
        hasDualBtn
        buttonTitle="Create"
        onClick={handleCreate}
        handleToggleModal={() => {
          setShowModal(false);
          setFolderName('');
          setParentId(null);
        }}
      />
    </>
  );
}

function TreeNode({ node, level, selectedFolder, onFolderSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(node.name);
  const isSelected = selectedFolder?.id === node.id;

  const dispatch = useDispatch();

  const handleRename = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === node.name || node.is_root) {
      setEditName(node.name);
      setIsEditing(false);
      return;
    }

    try {
      dispatch(renameFolderThunk(node.id, trimmed));
    } catch (error) {
      throw error;
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRename();
    if (e.key === 'Escape') {
      setEditName(node.name);
      setIsEditing(false);
    }
  };

  const [{ isOver }, drop] = useDrop({
    accept: 'document',
    drop: (item, monitor) => {
      // Prevent parent drop handlers from processing if a child already handled it
      if (monitor.didDrop()) {
        return;
      }

      if (node.id !== item.currentFolderId) {
        try {
          dispatch(moveDocumentThunk(item.id, node.id));

          //Auto focus folder
          onFolderSelect(node);
        } catch (error) {
          throw error;
        }
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }) && monitor.canDrop(),
    }),
  });

  useEffect(() => {
    //Open on hover during drag
    if (isOver && node.type === 'folder') {
      setIsOpen(true);
    }
  }, [isOver, node.type]);

  return (
    <div ref={drop} style={{ backgroundColor: isOver ? '#d5e4f7' : 'transparent' }}>
      {isEditing ? (
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={handleRename}
          onKeyDown={handleKeyDown}
          autoFocus
          onClick={(e) => e.stopPropagation()}
          style={{
            marginLeft: `${level * 20 + 32}px`,
            padding: '4px 8px',
            fontSize: '13.5px',
            border: '2px solid #4c81be',
            borderRadius: '4px',
            background: 'white',
            color: '#1e293b',
            outline: 'none',
          }}
        />
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (node.type === 'folder') setIsOpen(!isOpen);
            onFolderSelect(node);
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (!node.is_root) {
              setEditName(node.name);
              setIsEditing(true);
            }
          }}
          className={`
            ${styles.folderBtn}
            ${isSelected ? styles.selected : ''}
            ${node.is_root ? styles.root : ''}
          `}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          {node.type === 'folder' ? (
            <>
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <FaFolder size={24} color={node.is_root ? '#F59E0B' : '#4C81BE'} />
            </>
          ) : null}
          <span>{node.name}</span>
        </button>
      )}

      {node.type === 'folder' &&
        isOpen &&
        node.children?.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            level={level + 1}
            selectedFolder={selectedFolder}
            onFolderSelect={onFolderSelect}
          />
        ))}
    </div>
  );
}
